import {
    Box,
    FormHelperText,
    MenuItem,
    SelectProps as MUISelectProps,
    TextField,
} from '@mui/material';
import { ReactNode } from 'react';
import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { ErrorWidget } from 'app/agriculture/error_widget';
import './Select.scss';

interface DropDownOption {
    id: number | string;
    name: string;
    startAdornment?: ReactNode;
}
interface SelectProps<FormType extends FieldValues> {
    fieldName: Path<FormType>;
    options: DropDownOption[];
    noun: string;
    formController: UseFormReturn<FormType>;
    muiSelectProps?: MUISelectProps;
}

export function Select<FormType extends FieldValues>({
    fieldName,
    options,
    noun,
    formController,
    muiSelectProps,
}: SelectProps<FormType>) {
    const {
        getValues,
        control,
        formState: { errors },
    } = formController;

    return (
        <Controller
            name={fieldName}
            control={control}
            render={({ field: { ref, ...field } }) => (
                <>
                    <TextField
                        className="Select"
                        {...field}
                        inputRef={ref}
                        fullWidth
                        label={noun.toUpperCase()}
                        select
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                            select: {
                                classes: { root: 'Select__root', outlined: 'Select__outlined' },
                                renderValue: (value: unknown) => {
                                    const option = options.find((x) => x.id === value);
                                    return (
                                        <div className="Select__value">
                                            {option?.startAdornment} {option?.name}
                                        </div>
                                    );
                                },
                                defaultValue: getValues(fieldName),
                                ...muiSelectProps,
                            },
                            htmlInput: {
                                id: `entity-${fieldName}-input`,
                            },
                        }}
                    >
                        {options.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.startAdornment}
                                {option.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    {errors[fieldName]?.message == null ? null : (
                        <FormHelperText error>
                            <ErrorWidget
                                id={`entity-${fieldName}-error`}
                                errorMessage={<>{errors[fieldName].message}</>}
                            />
                        </FormHelperText>
                    )}
                </>
            )}
        />
    );
}
